import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Modal, Table, Dropdown } from "antd";
import { PlusOutlined, MoreOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import AddPretiumDoloris from "./AddPretiumDoloris";


const { confirm } = Modal;

const PretiumDoloris = () => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    }),
    []
  );

  const fetchAll = () => {
    axios
      .get(`${API_URL}/api/pretium-doloris`, { headers })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) => {
          if (a.pretiumDolorisId != null && b.pretiumDolorisId != null)
            return b.pretiumDolorisId - a.pretiumDolorisId;
          return String(a.qualification || "").localeCompare(String(b.qualification || ""));
        });
        setData(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des données.");
      });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleShow = (isEdit = false, item = null) => {
    setEditMode(isEdit);
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedItem(null);
  };

  const handleAdd = (created) => {
    setData((prev) =>
      [created, ...prev].sort((a, b) => {
        if (a.pretiumDolorisId != null && b.pretiumDolorisId != null)
          return b.pretiumDolorisId - a.pretiumDolorisId;
        return String(a.qualification || "").localeCompare(String(b.qualification || ""));
      })
    );
  };

  const handleUpdate = (updated) => {
    setData((prev) =>
      prev
        .map((it) => (it.pretiumDolorisId === updated.pretiumDolorisId ? updated : it))
        .sort((a, b) => {
          if (a.pretiumDolorisId != null && b.pretiumDolorisId != null)
            return b.pretiumDolorisId - a.pretiumDolorisId;
          return String(a.qualification || "").localeCompare(String(b.qualification || ""));
        })
    );
  };

  const confirmDelete = (id) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette ligne ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => doDelete(id),
    });
  };

  const doDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/pretium-doloris/delete/${id}`, { headers });
      setData((prev) => prev.filter((it) => it.pretiumDolorisId !== id));
      toast.success("Supprimé avec succès.");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const columns = [
    {
      title: "Qualification",
      dataIndex: "qualification",
      key: "qualification",
      sorter: (a, b) => String(a.qualification || "").localeCompare(String(b.qualification || "")),
    },
    {
      title: "Points",
      dataIndex: "points",
      key: "points",
      align: "right",
      sorter: (a, b) => Number(a.points || 0) - Number(b.points || 0),
    },
    {
      title: "Moral",
      dataIndex: "moral",
      key: "moral",
      align: "center",
      render: (v) => (v ? "Oui" : "Non"),
      sorter: (a, b) => Number(Boolean(a.moral)) - Number(Boolean(b.moral)),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          { key: "edit", label: "Modifier", onClick: () => handleShow(true, record) },
          { key: "delete", label: "Supprimer", danger: true, onClick: () => confirmDelete(record.pretiumDolorisId) },
        ];
        return (
          <Dropdown
            menu={{
              items: menuItems.map((it) => ({
                key: it.key,
                label: (
                  <span
                    style={it.danger ? { color: "#cf1322" } : {}}
                    onClick={(e) => {
                      e.preventDefault();
                      it.onClick();
                    }}
                  >
                    {it.label}
                  </span>
                ),
              })),
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleShow(false, null)}
                style={{ marginBottom: 16 }}
              >
                Ajouter
              </Button>

              <div className="card">
                <h5 className="card-header">Pretium Doloris</h5>
                <div className="table-responsive text-nowrap p-3">
                  <Table
                    rowKey={(r) => r.pretiumDolorisId}
                    dataSource={data}
                    columns={columns}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    size="middle"
                  />
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </div>
      </div>

      <AddPretiumDoloris
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedItem={selectedItem}
        items={data}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default PretiumDoloris;
